import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import { BRAND } from '@yuan-math/config';
import { Math } from '@yuan-math/ui';
import { registry } from '@yuan-math/content';

/** What the product is and what it refuses to be. */
export function AboutPage(): JSX.Element {
  const concepts = registry.concepts.length;
  const available = registry.availableConcepts().length;
  const edges = registry.concepts.reduce((sum, concept) => sum + concept.prerequisites.length, 0);

  return (
    <div className="page page--reading">
      <header className="page-header">
        <h1 className="page__title">About {BRAND.name}</h1>
        <p className="page__lead">{BRAND.tagline}</p>
      </header>

      <div className="prose-column">
        <p className="prose">
          Most mathematics websites are libraries: a great deal of material, organised by school year, waiting for you
          to know what you need. That works if you already know what you are missing. Most students do not — a
          fourteen-year-old struggling with algebra is usually struggling with fractions, and nobody has told them.
        </p>

        <p className="prose">
          {BRAND.name} is built the other way round. Underneath everything is a graph of {concepts} mathematical
          concepts joined by {edges} prerequisite relationships, from counting through to topology. Lessons,
          exercises, progress and eventually an AI tutor all sit on top of that graph. It is the product; the website
          is how you use it.
        </p>

        <h2 className="prose-heading">What that changes</h2>

        <p className="prose">
          Because every concept knows what it depends on, the platform can do things a library cannot. It can tell
          you that adding fractions is failing because common denominators never quite settled. It can plan the
          shortest legal route from where you are to calculus. It can bring a concept back three weeks later,
          precisely when you were about to forget it.
        </p>

        <p className="prose">
          And because a wrong answer is treated as information rather than a verdict, it can name what you did.
          Answer <Math latex="\tfrac{1}{2} + \tfrac{1}{3}" /> with <Math latex="\tfrac{2}{5}" /> and you have not
          simply failed — you have added the denominators, which is a specific and very reasonable mistake with a
          specific repair.
        </p>

        <h2 className="prose-heading">What it measures</h2>

        <p className="prose">
          Not completion. Nothing on this site will ever congratulate you for finishing Grade 5. It measures mastery,
          and it measures it as four separate things: whether you understand the idea, whether you get it right,
          whether you still get it right a month later, and whether you can use it on a problem that does not
          announce which method to apply.
        </p>

        <h2 className="prose-heading">Where it is</h2>

        <p className="prose">
          {available} concepts are fully written today — the Grades 3 to 5 slice, deliberately built end to end
          rather than spread thin. The remaining {concepts - available} are on the map with their prerequisites
          already in place, because the architecture had to prove it could carry university mathematics before the
          content arrived, not after.
        </p>

        <h2 className="prose-heading">What it will not do</h2>

        <p className="prose">
          It will not advertise to children, sell their data, or measure them against each other. It will not ship an
          AI tutor before the deterministic system underneath it works. And it will not confuse a streak with an
          education.
        </p>

        <h2 className="prose-heading">Who made it</h2>

        <p className="prose">
          {BRAND.name} was created and founded by <strong>Ethan Yuan</strong>, who is also its CEO. The goal is a
          website where everyone can learn mathematics, whatever level they are starting from.
        </p>

        <div className="button-row">
          <Link to="/map" className="btn btn--primary">
            See the map
          </Link>
          <Link to="/start" className="btn btn--secondary">
            Start learning
          </Link>
        </div>
      </div>
    </div>
  );
}
